// Enhanced logging utility with level filtering and console grouping
// Provides clean, organized console output for TSL debugging

// LogLevel as const object for erasableSyntaxOnly compatibility
export const LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
} as const;

export type LogLevel = typeof LogLevel[keyof typeof LogLevel];

// LogLevel name lookup for display purposes
const LogLevelNames: Record<LogLevel, string> = {
  [LogLevel.ERROR]: 'ERROR',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.DEBUG]: 'DEBUG',
};

export interface LoggerConfig {
  level: LogLevel;
  enableGrouping: boolean;
  enableTimestamps: boolean;
  prefix?: string;
}

class Logger {
  protected config: LoggerConfig = {
    level: LogLevel.INFO,
    enableGrouping: true,
    enableTimestamps: true,
    prefix: 'TSL'
  };

  constructor(config?: Partial<LoggerConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }

    // Load log level from localStorage
    const savedLevel = localStorage.getItem('mexc_log_level');
    if (savedLevel && !isNaN(Number(savedLevel))) {
      this.config.level = Number(savedLevel) as LogLevel;
    }
  }

  setLevel(level: LogLevel): void {
    this.config.level = level;
    localStorage.setItem('mexc_log_level', level.toString());
    console.log(`🔧 Log level set to: ${LogLevelNames[level]}`);
  }

  getLevel(): LogLevel {
    return this.config.level;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.config.level;
  }

  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = this.config.enableTimestamps
      ? new Date().toISOString().substr(11, 12)
      : '';
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';
    const levelStr = LogLevelNames[level];

    return `${timestamp} ${prefix} ${levelStr}: ${message}`;
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`❌ ${this.formatMessage(LogLevel.ERROR, message)}`, ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`⚠️ ${this.formatMessage(LogLevel.WARN, message)}`, ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(`ℹ️ ${this.formatMessage(LogLevel.INFO, message)}`, ...args);
    }
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`🐛 ${this.formatMessage(LogLevel.DEBUG, message)}`, ...args);
    }
  }

  group(title: string, level: LogLevel = LogLevel.INFO): void {
    if (this.shouldLog(level) && this.config.enableGrouping) {
      console.group(title);
    }
  }

  groupCollapsed(title: string, level: LogLevel = LogLevel.INFO): void {
    if (this.shouldLog(level) && this.config.enableGrouping) {
      console.groupCollapsed(title);
    }
  }

  groupEnd(level: LogLevel = LogLevel.INFO): void {
    if (this.shouldLog(level) && this.config.enableGrouping) {
      console.groupEnd();
    }
  }

  // Special methods for TSL-specific logging
  tslInfo(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(`🎯 ${this.formatMessage(LogLevel.INFO, message)}`, ...args);
    }
  }

  tslDebug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`🔄 ${this.formatMessage(LogLevel.DEBUG, message)}`, ...args);
    }
  }

  // TSL Processing cycle logging with grouping
  tslProcessingStart(cycleType: 'scheduled' | 'forced' = 'scheduled'): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const icon = cycleType === 'forced' ? '⚡' : '🔄';
      const title = `${icon} TSL Processing Cycle (${cycleType.toUpperCase()}) - ${new Date().toISOString()}`;
      this.group(title, LogLevel.INFO);
    }
  }

  tslProcessingEnd(success: boolean = true): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const icon = success ? '✅' : '❌';
      console.log(`${icon} TSL processing cycle completed`);
      this.groupEnd(LogLevel.INFO);
    }
  }

  // Position-specific TSL logging
  tslPositionStart(symbol: string, positionId: number): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.groupCollapsed(`📊 Processing ${symbol} (Position ${positionId})`, LogLevel.DEBUG);
    }
  }

  tslPositionEnd(): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.groupEnd(LogLevel.DEBUG);
    }
  }

  // Critical TSL events (always shown unless ERROR level)
  tslCritical(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.log(`🚨 ${this.formatMessage(LogLevel.WARN, message)}`, ...args);
    }
  }

  // TSL adjustment logging
  tslAdjustment(symbol: string, oldPrice?: number, newPrice?: number, reason?: string): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const priceChange = oldPrice && newPrice ? ` (${oldPrice.toFixed(4)} → ${newPrice.toFixed(4)})` : '';
      const reasonText = reason ? ` - ${reason}` : '';
      console.log(`📈 TSL Adjustment: ${symbol}${priceChange}${reasonText}`);
    }
  }

  // Rate limiting map for priceUpdate method
  private static priceUpdateLastLog: Map<string, { price: number; ts: number }> = new Map();

  priceUpdate(symbol: string, price: number, priority: 'high' | 'medium' | 'low' = 'low'): void {
    const level = priority === 'high' ? LogLevel.INFO : LogLevel.DEBUG;
    if (!this.shouldLog(level)) return;

    // Apply rate limiting - suppress duplicate prices within 5 seconds
    const now = Date.now();
    const last = Logger.priceUpdateLastLog.get(symbol);
    if (last && last.price === price && now - last.ts < 5000) {
      return; // suppress duplicate
    }

    // For non-high priority, apply stricter rate limiting (max 1 log per 30 seconds per symbol)
    if (priority !== 'high') {
      if (last && now - last.ts < 30000) {
        return; // rate limit non-priority updates
      }
    }

    Logger.priceUpdateLastLog.set(symbol, { price, ts: now });

    const icon = priority === 'high' ? '🎯' : '💰';
    console.log(`${icon} Price update for ${symbol}: ${price}`);
  }
}

// Ticker filtering configuration
interface TickerFilterConfig {
  enableFiltering: boolean;
  maxLogsPerSymbolPerMinute: number;
  prioritySymbols: Set<string>; // TSL-enabled symbols get priority
  lastLogTimes: Map<string, number>;
}

class TickerLogger extends Logger {
  private filterConfig: TickerFilterConfig = {
    enableFiltering: true,
    maxLogsPerSymbolPerMinute: 2,
    prioritySymbols: new Set(),
    lastLogTimes: new Map(),
  };
  private lastHighPriorityLog: Map<string, { price: number; ts: number }> = new Map();

  constructor(config?: Partial<LoggerConfig>) {
    super({ ...config, prefix: 'TICKER' });

    // Load filtering preference
    const filteringEnabled = localStorage.getItem('mexc_ticker_filtering') !== 'false';
    this.filterConfig.enableFiltering = filteringEnabled;
  }

  // Update priority symbols (TSL-enabled positions)
  updatePrioritySymbols(symbols: string[]): void {
    this.filterConfig.prioritySymbols = new Set(symbols);
  }

  // Toggle ticker filtering
  toggleFiltering(): boolean {
    this.filterConfig.enableFiltering = !this.filterConfig.enableFiltering;
    localStorage.setItem('mexc_ticker_filtering', this.filterConfig.enableFiltering.toString());

    console.log(`🔧 Ticker filtering ${this.filterConfig.enableFiltering ? 'enabled' : 'disabled'}`);
    if (this.filterConfig.enableFiltering) {
      console.log('📝 Only showing ticker updates for TSL-enabled positions and rate-limited others');
    } else {
      console.log('📝 Showing ALL ticker updates (debug mode)');
    }

    return this.filterConfig.enableFiltering;
  }

  // Smart ticker logging with filtering
  tickerUpdate(symbol: string, price: number, source: 'api' | 'cache' = 'api', priority: 'high' | 'medium' | 'low' = 'medium'): void {
    if (this.config.level < LogLevel.INFO) return;

    const now = Date.now();

    // Skip filtering if disabled (debug mode) but still dedupe spammy duplicates
    if (!this.filterConfig.enableFiltering) {
      const last = this.lastHighPriorityLog.get(symbol);
      if (last && last.price === price && now - last.ts < 2000) {
        return; // suppress identical duplicates within 2s
      }
      this.lastHighPriorityLog.set(symbol, { price, ts: now });
      const icon = source === 'cache' ? '💾' : '📊';
      console.log(`${icon} Price update for ${symbol}: ${price}`);
      return;
    }

    // High priority (TSL-enabled positions) with de-duplication across hook instances
    if (priority === 'high' || this.filterConfig.prioritySymbols.has(symbol)) {
      const last = this.lastHighPriorityLog.get(symbol);
      if (last && last.price === price && now - last.ts < 2000) {
        return; // suppress burst duplicates
      }
      this.lastHighPriorityLog.set(symbol, { price, ts: now });
      const icon = '🔥'; // High priority icon
      console.log(`${icon} TSL Price update for ${symbol}: ${price} (${source})`);
      return;
    }

    // Rate limiting for other symbols
    const lastLogTime = this.filterConfig.lastLogTimes.get(symbol) || 0;
    const timeSinceLastLog = now - lastLogTime;
    const minInterval = 60000 / this.filterConfig.maxLogsPerSymbolPerMinute; // Convert to ms

    if (timeSinceLastLog >= minInterval) {
      this.filterConfig.lastLogTimes.set(symbol, now);
      const icon = source === 'cache' ? '💾' : '📊';
      console.log(`${icon} Price update for ${symbol}: ${price} (${source})`);
    }
  }

  // Get filtering status
  getFilteringStatus(): { enabled: boolean; prioritySymbols: string[]; rateLimitMs: number } {
    return {
      enabled: this.filterConfig.enableFiltering,
      prioritySymbols: Array.from(this.filterConfig.prioritySymbols),
      rateLimitMs: 60000 / this.filterConfig.maxLogsPerSymbolPerMinute,
    };
  }
}

// Create default logger instances
export const tslLogger = new Logger({ prefix: 'TSL' });
export const apiLogger = new Logger({ prefix: 'API' });
export const tickerLogger = new TickerLogger();

// Global functions for console access
export function setLogLevel(level: LogLevel | string): void {
  const logLevel = typeof level === 'string'
    ? LogLevel[level.toUpperCase() as keyof typeof LogLevel]
    : level;

  if (logLevel !== undefined) {
    tslLogger.setLevel(logLevel);
    console.log(`🔧 Global log level set to: ${LogLevelNames[logLevel]}`);
  } else {
    console.error('❌ Invalid log level. Use: ERROR, WARN, INFO, or DEBUG');
  }
}

export function getLogLevel(): string {
  return LogLevelNames[tslLogger.getLevel()];
}

export function showLogLevels(): void {
  console.group('📊 Available Log Levels');
  console.log('ERROR (0) - Only errors');
  console.log('WARN (1) - Errors and warnings');
  console.log('INFO (2) - Errors, warnings, and info (default)');
  console.log('DEBUG (3) - All messages including debug');
  console.log('');
  console.log('Current level:', getLogLevel());
  console.log('');
  console.log('Usage: setLogLevel("DEBUG") or setLogLevel(3)');
  console.groupEnd();
}

// Ticker filtering control functions
export function toggleTickerFiltering(): boolean {
  return tickerLogger.toggleFiltering();
}

export function getTickerFilteringStatus(): { enabled: boolean; prioritySymbols: string[]; rateLimitMs: number } {
  return tickerLogger.getFilteringStatus();
}

export function showTickerFilteringHelp(): void {
  console.group('📊 Ticker Filtering Help');
  console.log('Current status:', getTickerFilteringStatus());
  console.log('');
  console.log('Commands:');
  console.log('  toggleTickerFiltering() - Enable/disable intelligent filtering');
  console.log('  getTickerFilteringStatus() - Show current filtering configuration');
  console.log('  showTickerFilteringHelp() - Show this help');
  console.log('');
  console.log('Filtering behavior:');
  console.log('  🔥 High Priority: TSL-enabled positions (always shown)');
  console.log('  📈 Medium Priority: Active positions (rate limited)');
  console.log('  📊 Low Priority: Other symbols (heavily filtered)');
  console.log('');
  console.log('When filtering is disabled, ALL ticker updates are shown.');
  console.groupEnd();
}

// Make functions available globally
if (typeof window !== 'undefined') {
  (window as any).setLogLevel = setLogLevel;
  (window as any).getLogLevel = getLogLevel;
  (window as any).showLogLevels = showLogLevels;
  (window as any).toggleTickerFiltering = toggleTickerFiltering;
  (window as any).getTickerFilteringStatus = getTickerFilteringStatus;
  (window as any).showTickerFilteringHelp = showTickerFilteringHelp;
}
